import { useSelectableList } from '@fullstory/react-hooks';
import { noop } from 'lodash';
import React, { ComponentProps, memo, RefObject, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { LazyLoadList } from '../../components/LazyLoadList';
import { ListDivider } from '../../components/ListDivider';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { Select } from '../Select';
import { OptionType, SelectMenuHandle } from '../SelectMenu/types';
import { SelectMenuTooltip } from '../SelectMenuTooltip';
import { LAZY_LOAD_THRESHOLD } from './constants';
import { useScrollToHandle } from './useScrollToHandle';

const MemoizedOption = memo(Select.Option) as typeof Select.Option;

export interface SelectMenuOptionsProps<T> {
  /**
   * Allows ungrouped options to be displayed alongside grouped options.
   * If false, ungrouped options are ignored and a warning is logged.
   */
  allowUngrouped?: boolean;
  /**
   * If true, the group sorting is trusted.
   * A given group may be rendered multiple times if it appears with other groups in between.
   */
  allowUnsortedGroups?: boolean;
  /**
   * If true, the options are displayed with dividers between groups.
   */
  groupDividers?: boolean;
  /**
   * The array of options to be displayed in the list of options.
   */
  options: OptionType<T>[];
  /**
   * The callback for when a menu option is clicked.
   */
  onClick: (option: OptionType<T> | null) => void;
  /**
   * Optional callback for when a menu option is highlighted.
   */
  onHighlight?: (option: OptionType<T> | null) => void;
  /**
   * If true, tab will select a menu item.
   */
  tabSelects?: boolean;
  /**
   * A prop passed through to the list component that wraps options. If true, the options have a more compact padding.
   */
  compact?: boolean;
  /**
   * If true, allows the user to clear the selected item.
   */
  clearable?: boolean;
  /**
   * By default (`false`), the selected option is not hoisted to the top unless `clearable` is set to `true`.
   * If `hoistSelected` is set to `true` then the selected option is hoisted regardless of what `clearable` is
   * set to.
   */
  hoistSelected?: boolean;
  /**
   * If true, prevents option hover state from being cleared on mouse leave.
   */
  alwaysHovered?: boolean;
  /**
   * The selected option.
   */
  selected?: OptionType<T> | null;
  /**
   * If provided, controls the display of the tooltips provided in the `Option`.
   */
  tooltipProps?: Pick<
    ComponentProps<typeof SelectMenuTooltip>,
    'placement' | 'maxWidth' | 'variant'
  >;
  /**
   * Optional ref that enables opening the menu and scrolling to an option or group.
   *
   * Note: ideally we would use `React.forwardRef()` to forward the ref rather than an
   * `innerRef` prop, but `MultiSelectMenu` uses a generic props type which is not
   * supported by `React.forwardRef()`.
   */
  innerRef?: RefObject<SelectMenuHandle>;
}

export function SelectMenuOptions<T>({
  options,
  groupDividers,
  selected,
  onClick,
  onHighlight,
  compact,
  clearable,
  hoistSelected = false,
  alwaysHovered,
  tooltipProps,
  innerRef,
  tabSelects,
}: SelectMenuOptionsProps<T>) {
  const intl = useIntl();
  /**
   * If the selected option can be cleared - then we want to hoist it to the top -
   * otherwise we don't want to.
   */
  const hoistSelectedToTop = hoistSelected || clearable;
  const [optionsRef, setOptionsRef] = useState<HTMLDivElement | null>(null);

  const nonHoistedOptions = useMemo(
    () =>
      selected && hoistSelectedToTop ? options.filter(opt => opt.key !== selected.key) : options,
    [hoistSelectedToTop, selected, options],
  );

  const { getListItemProps } = useSelectableList<HTMLLIElement, OptionType<T>>(
    useMemo(
      () => (selected && hoistSelectedToTop ? [selected, ...nonHoistedOptions] : nonHoistedOptions),
      [selected, nonHoistedOptions, hoistSelectedToTop],
    ),
    (maybeOption, option) => maybeOption.key === option.key,
    onClick,
    {
      autoScroll: true,
      alwaysHovered,
      // SelectMenuOptions are only rendered when the portal is open, which means active should always be true
      active: true,
    },
    onHighlight,
    tabSelects,
  );

  // implement imperative scroll to handlers
  const { groupRef, optionRef } = useScrollToHandle(innerRef);

  return (
    <Box
      data-component="SelectMenuOptions"
      overflow="auto"
      flexGrow={1}
      ref={setOptionsRef}
      {...identifyComponent('select-menu-options')}
    >
      <LazyLoadList
        numOfItems={options.length}
        active={options.length > LAZY_LOAD_THRESHOLD}
        initialPageSize={LAZY_LOAD_THRESHOLD}
        rootRef={optionsRef}
      >
        {({ shouldRenderIndex }) => (
          <Select.Options>
            {selected && hoistSelectedToTop && (
              <>
                <Select.OptionGroupHeader
                  compact={compact}
                  fixed={false}
                  actions={
                    clearable
                      ? [
                          {
                            label: intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' }),
                            onClick: () => onClick(null),
                          },
                        ]
                      : undefined
                  }
                >
                  Selected
                </Select.OptionGroupHeader>
                <MemoizedOption
                  selected
                  leftIcon={selected.icon}
                  rightAdornment={
                    selected.rightAdornment ||
                    (selected.tooltip && (
                      <SelectMenuTooltip tooltipContent={selected.tooltip} {...tooltipProps} />
                    ))
                  }
                  subtext={selected.description}
                  disabled={selected.disabled}
                  compact={compact}
                  testId={selected.testId}
                  {...getListItemProps(selected)}
                  // deselection is handled by the clear button
                  onClick={noop}
                >
                  {selected.richTextLabel ?? selected.label}
                </MemoizedOption>
                <ListDivider />
              </>
            )}
            {nonHoistedOptions.map((option, index) => {
              // Offset the index passed to `shouldRenderIndex` if `hoistSelected` and `selected.
              // This is because this option will be removed from `nonHoistedOptions` and hoisted to the top.
              // Otherwise, the selected option remains in the list so don't offset.
              if (!shouldRenderIndex(selected && hoistSelectedToTop ? index + 1 : index))
                return null;
              /**
               * Render the group header if it's a new group, or if the previous option was
               * The selected option (which will always be first). In that case, we want to render the
               * group header again since it's only fixed when it's not the selected.
               */
              const prevOption = nonHoistedOptions[index - 1];
              const renderGroupHeader = option.group && prevOption?.group !== option.group;
              const isSelected = option.key === selected?.key;
              const { ref: selectedRef, ...listItemProps } = getListItemProps(option);

              return (
                <React.Fragment key={option.key}>
                  {renderGroupHeader && (
                    <>
                      {groupDividers && index > 0 && <ListDivider />}
                      <Select.OptionGroupHeader ref={groupRef(option)} compact={compact}>
                        {option.group}
                      </Select.OptionGroupHeader>
                    </>
                  )}
                  <MemoizedOption
                    selected={isSelected}
                    leftIcon={option.icon}
                    leftAdornment={option.leftAdornment}
                    rightAdornment={
                      option.rightAdornment ||
                      (option.tooltip && (
                        <SelectMenuTooltip tooltipContent={option.tooltip} {...tooltipProps} />
                      ))
                    }
                    subtext={option.description}
                    disabled={option.disabled}
                    compact={compact}
                    ref={selectedRef ?? optionRef(option)}
                    testId={option.testId}
                    {...listItemProps}
                  >
                    {option.richTextLabel ?? option.label}
                  </MemoizedOption>
                </React.Fragment>
              );
            })}
          </Select.Options>
        )}
      </LazyLoadList>
    </Box>
  );
}
